import React, {useState, useEffect, useCallback, useRef} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Dropdown from 'react-bootstrap/Dropdown';
import InputGroup from 'react-bootstrap/InputGroup';
import Spinner from '@/components/ui/Spinner';
import {Icon} from '@/components/icons/Icon';
import axios from 'axios';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import i18n from 'i18n';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';

interface Entity {
  id: number;
  name: string;
  displayName?: string;
  externalId?: string;
  shortname?: string;
}

interface EntitySelectorProps {
  entityType: 'agents' | 'teams' | 'customers' | 'channels';
  currentEntityId?: number;
  currentEntityName?: string;
  onEntityChange?: (entityId: number, entityName: string) => void;
}

const getEntityConfig = (entityType: string) => {
    const configs = {
        agents: {
            apiUrl: window.URLS.agentsResource,
            nameField: 'fullName',
            routePattern: window.URLS.agentDetailView,
            paramName: 'agentId',
            displayName: 'Agent',
            icon: 'mdi-account-tie',
            searchIcon: 'mdi-account-search',
            identifierField: 'externalId'
        },
        teams: {
            apiUrl: window.URLS.teamsResource,
            nameField: 'name',
            routePattern: window.URLS.teamDetailView,
            paramName: 'teamId',
            displayName: 'Team',
            icon: 'mdi-account-group',
            searchIcon: 'mdi-account-group-outline',
            identifierField: 'shortname'
        },
        customers: {
            apiUrl: window.URLS.customersListResource,
            nameField: 'fullName',
            routePattern: window.URLS.customerDetailView,
            paramName: 'customerId',
            displayName: 'Customer',
            icon: 'mdi-account',
            searchIcon: 'mdi-account-search-outline',
            identifierField: 'externalId'
        },
        channels: {
            apiUrl: window.URLS.channelsResource,
            nameField: 'name',
            routePattern: window.URLS.adminChannelDetailView,
            paramName: 'channelId',
            displayName: 'Channel',
            icon: 'mdi-pound',
            searchIcon: 'mdi-pound-box-outline',
            identifierField: 'shortname'
        }
    };
    return configs[entityType as keyof typeof configs];
};

const getEntityIdentifier = (entity: any, entityType: string): string => {
    const config = getEntityConfig(entityType);
    if (!config) {return entity.id.toString();}
  
    const identifier = entity[config.identifierField];
    return identifier || entity.id.toString();
};

const searchEntities = async (apiUrl: string, query: string): Promise<Entity[]> => {
    if (!apiUrl) {return [];}

    try {
        const response = await axios.get(apiUrl, {
            params: {query, limit: 50}
        });

        // Handle different API response structures
        const data = response.data;

        // Check for paginated response structure
        if (data.items && Array.isArray(data.items)) {
            return data.items;
        }

        // Check for direct array response
        if (Array.isArray(data)) {
            return data;
        }

        // Check for results array
        if (data.results && Array.isArray(data.results)) {
            return data.results;
        }

        console.warn('Unexpected API response structure:', data);
        return [];
    } catch (error) {
        console.error('Failed to search entities:', error);
        return [];
    }
};

const searchEntitiesDebounced = AwesomeDebouncePromise(searchEntities, 300);

const EntitySelector: React.FC<EntitySelectorProps> = ({
    entityType,
    currentEntityId,
    currentEntityName,
    onEntityChange
}) => {
    const {userData} = useOptimizedConfig();
    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];
    const entityNameRestricted = (
        (entityType === 'agents' && restrictedFieldKeys.includes('agent_pii')) ||
        (entityType === 'teams' && restrictedFieldKeys.includes('team_pii')) ||
        (entityType === 'customers' && restrictedFieldKeys.includes('customer_pii'))
    );

    const [searchQuery, setSearchQuery] = useState('');
    const [entities, setEntities] = useState<Entity[]>([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    // Initialize cache from localStorage to persist across component re-mounts
    const [selectedEntityCache, setSelectedEntityCache] = useState<{ [key: number]: string }>(() => {
        try {
            const cached = localStorage.getItem(`entitySelector_${entityType}_cache`);
            return cached ? JSON.parse(cached) : {};
        } catch {
            return {};
        }
    });
    const navigate = useNavigate();
    const location = useLocation();

    const config = getEntityConfig(entityType);

    if (!config) {
        return null;
    }

    const fetchEntities = useCallback(async (query: string) => {
        if (!config.apiUrl) {return;}

        setLoading(true);
        try {
            const results = await searchEntitiesDebounced(config.apiUrl, query);

            const mappedEntities = results.map((entity: any) => {
                const name = entityNameRestricted
                    ? `${config.displayName} #${entity.id}`
                    : (entity[config.nameField] || entity.name || `${config.displayName} ${entity.id}`);

                const identifier = entityNameRestricted
                    ? `${entity.id}`
                    : getEntityIdentifier(entity, entityType);
                const displayName = entityNameRestricted
                    ? name
                    : `${name} (${identifier})`;
        
                return {
                    id: entity.id,
                    name: name,
                    displayName: displayName,
                    externalId: entity.externalId,
                    shortname: entity.shortname
                };
            });

            setEntities(mappedEntities);
        } catch (error) {
            console.error('Failed to fetch entities:', error);
            setEntities([]);
        } finally {
            setLoading(false);
        }
    }, [config.apiUrl, config.nameField, config.displayName, entityNameRestricted]);

    useEffect(() => {
        if (showDropdown) {
            fetchEntities(searchQuery);
        }
    }, [searchQuery, showDropdown, fetchEntities]);

    // Fetch current entity details if we have ID but no cached name
    useEffect(() => {
        const fetchCurrentEntity = async () => {
            if (currentEntityId && !currentEntityName && !selectedEntityCache[currentEntityId] && config.apiUrl) {
                try {
                    const response = await axios.get(`${config.apiUrl.replace('?', '')}/${currentEntityId}`);
                    const entity = response.data;

                    if (entity) {
                        const name = entityNameRestricted
                            ? `${config.displayName} #${entity.id}`
                            : (entity[config.nameField] || entity.name || `${config.displayName} ${entity.id}`);
                        const identifier = entityNameRestricted
                            ? `${entity.id}`
                            : getEntityIdentifier(entity, entityType);
                        const displayName = entityNameRestricted
                            ? name
                            : `${name} (${identifier})`;
            
                        const newCache = {
                            ...selectedEntityCache,
                            [entity.id]: displayName
                        };
                        setSelectedEntityCache(newCache);

                        try {
                            localStorage.setItem(`entitySelector_${entityType}_cache`, JSON.stringify(newCache));
                        } catch (error) {
                            console.warn('Failed to save entity cache to localStorage:', error);
                        }
                    }
                } catch (error) {
                    console.error('Failed to fetch current entity:', error);
                }
            }
        };

        fetchCurrentEntity();
    }, [currentEntityId, currentEntityName, selectedEntityCache, config.apiUrl, config.nameField, config.displayName, entityType, entityNameRestricted]);


    const handleEntitySelect = (entity: Entity) => {
        setShowDropdown(false);
        setSearchQuery('');

        // Cache the selected entity's display name
        const identifier = getEntityIdentifier(entity, entityType);
        const displayName = entity.displayName || `${entity.name} (${identifier})`;
        const newCache = {
            ...selectedEntityCache,
            [entity.id]: displayName
        };
        setSelectedEntityCache(newCache);

        // Persist to localStorage
        try {
            localStorage.setItem(`entitySelector_${entityType}_cache`, JSON.stringify(newCache));
        } catch (error) {
            console.warn('Failed to save entity cache to localStorage:', error);
        }

        if (onEntityChange) {
            onEntityChange(entity.id, entity.name);
        } else {
            // Navigate to the new entity, preserving the current tab
            const currentPath = location.pathname;
            const pathParts = currentPath.split('/');

            // Find and replace the entity ID in the current path
            const newPathParts = pathParts.map((part, index) => {
                // Check if this part is a numeric ID that should be replaced
                if (!isNaN(parseInt(part)) && part === currentEntityId?.toString()) {
                    return entity.id.toString();
                }
                return part;
            });

            const newPath = newPathParts.join('/');
            navigate(newPath);
        }
    };

    // Use a ref to store the last known good display name to prevent flickering
    const lastDisplayNameRef = useRef<string>('');

    const getDisplayName = () => {
        if (entityNameRestricted && currentEntityId) {
            return `${config.displayName} #${currentEntityId}`;
        }

    // Check cache first (including localStorage cache)
        if (currentEntityId && selectedEntityCache[currentEntityId]) {
            const cachedName = selectedEntityCache[currentEntityId];
            lastDisplayNameRef.current = cachedName;
            return cachedName;
        }

        // If we have a last known good display name for the same entity, use it immediately
        if (currentEntityId && lastDisplayNameRef.current && 
        (lastDisplayNameRef.current.includes(`(${currentEntityId})`) || 
         lastDisplayNameRef.current.match(/\([^)]+\)$/))) {
            return lastDisplayNameRef.current;
        }

        // If currentEntityName and currentEntityId are provided, format as "Name (ID)"
        // Note: We can't easily get the proper identifier here without the full entity data,
        // so we'll rely on the cache being populated by the fetch operations above
        if (currentEntityName && currentEntityId) {
            const result = currentEntityName.includes('(') 
                ? currentEntityName 
                : `${currentEntityName} (${currentEntityId})`;
            lastDisplayNameRef.current = result;
            return result;
        }

        // Just the name if available
        if (currentEntityName) {
            return currentEntityName;
        }

        // Only show fallback if we really have no other option
        if (currentEntityId) {
            return `${config.displayName} ${currentEntityId}`;
        }

        // Default selection text
        return `Select ${config.displayName}`;
    };

    const displayName = getDisplayName();

    return (
        <div className="entity-selector mb-3">
            <Dropdown show={showDropdown} onToggle={setShowDropdown}>
                <Dropdown.Toggle
                    as="button"
                    className="btn btn-outline-info d-flex align-items-center justify-content-between"
                    bsPrefix="custom-dropdown-toggle"
                    aria-label={`Select ${config.displayName.toLowerCase()}: ${displayName}`}
                    aria-haspopup="listbox"
                    aria-expanded={showDropdown}
                    style={{
                        minWidth: '320px',
                        maxWidth: '600px',
                        width: 'auto',
                        border: '1px solid #009efb',
                        borderRadius: '6px',
                        padding: '10px 16px',
                        fontSize: '14px',
                        fontWeight: '400',
                        color: '#54667a',
                        backgroundColor: '#fff',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        overflow: 'hidden'
                    }}
                >
                    <div className="d-flex align-items-center flex-grow-1">
                        <Icon
                            name={config.icon}
                            size="sm"
                            className="mr-2 d-flex align-items-center"
                            style={{color: '#009efb', opacity: 0.7}}
                        />
                        <span className="text-truncate" style={{color: '#54667a'}}>
                            {displayName}
                        </span>
                    </div>
                    <Icon
                        name="mdi-chevron-down"
                        size="sm"
                        style={{
                            color: '#009efb',
                            opacity: 0.7,
                            transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                        }}
                    />
                </Dropdown.Toggle>

                <Dropdown.Menu
                    style={{
                        minWidth: '320px',
                        maxWidth: '400px',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        padding: '0',
                        marginTop: '4px'
                    }}
                >
                    <div style={{padding: '16px 16px 12px'}}>
                        <div className="position-relative">
                            <Icon
                                name="mdi-magnify"
                                size="sm"
                                className="position-absolute"
                                style={{
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#a6b7bf',
                                    zIndex: 2
                                }}
                            />
                            <Form.Control
                                type="text"
                                placeholder={entityNameRestricted
                                    ? i18n.t('jt_search_disabled_restricted.text', 'Search not available')
                                    : i18n.t('jt_search_placeholder.text', {displayName: config.displayName.toLowerCase()})
                                }
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                autoFocus={!entityNameRestricted}
                                disabled={entityNameRestricted}
                                aria-label={`Search ${config.displayName.toLowerCase()}`}
                                role="searchbox"
                                style={{
                                    paddingLeft: '40px',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    height: '40px',
                                    backgroundColor: '#f8f9fa',
                                    transition: 'all 0.2s ease'
                                }}
                                onFocus={(e) => {
                                    e.target.style.backgroundColor = '#fff';
                                    e.target.style.borderColor = '#009efb';
                                    e.target.style.boxShadow = '0 0 0 2px rgba(0, 158, 251, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.backgroundColor = '#f8f9fa';
                                    e.target.style.borderColor = '#dee2e6';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                        </div>
                    </div>

                    <div style={{borderTop: '1px solid #f1f3f4'}} />

                    <div
                        role="listbox"
                        aria-label={`${config.displayName} options`}
                        style={{
                            maxHeight: '280px',
                            overflowY: 'auto',
                            padding: '8px 0'
                        }}
                    >
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center" style={{padding: '32px'}}>
                                <Spinner size="sm" variant="border" color="info" />
                                <span className="ml-2 text-muted" style={{fontSize: '14px'}}>
                                    {i18n.t('jt_searching.text', 'Searching...')}
                                </span>
                            </div>
                        ) : entities.length > 0 ? (
                            entities.map((entity) => (
                                <div
                                    key={entity.id}
                                    onClick={() => handleEntitySelect(entity)}
                                    className={`dropdown-item-custom ${entity.id === currentEntityId ? 'active' : ''}`}
                                    role="option"
                                    aria-selected={entity.id === currentEntityId}
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleEntitySelect(entity);
                                        }
                                    }}
                                    style={{
                                        padding: '12px 20px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        color: entity.id === currentEntityId ? '#fff' : '#54667a',
                                        backgroundColor: entity.id === currentEntityId ? '#009efb' : 'transparent',
                                        borderRadius: '0',
                                        transition: 'all 0.15s ease',
                                        borderLeft: entity.id === currentEntityId ? '3px solid #007acc' : '3px solid transparent'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (entity.id !== currentEntityId) {
                                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                                            e.currentTarget.style.borderLeftColor = '#009efb';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (entity.id !== currentEntityId) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.borderLeftColor = 'transparent';
                                        }
                                    }}
                                >
                                    <div className="d-flex align-items-center">
                                        <Icon
                                            name={config.icon}
                                            size="sm"
                                            className="mr-2"
                                            style={{
                                                color: entity.id === currentEntityId ? '#fff' : '#009efb',
                                                opacity: 0.8
                                            }}
                                        />
                                        <span className="text-truncate">
                                            {entity.displayName}
                                        </span>
                                        {entity.id === currentEntityId && (
                                            <Icon
                                                name="mdi-check"
                                                size="sm"
                                                className="ml-auto"
                                                style={{color: '#fff'}}
                                            />
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : searchQuery ? (
                            <div className="text-center" style={{padding: '32px 20px'}}>
                                <Icon
                                    name="mdi-magnify"
                                    size="lg"
                                    style={{color: '#a6b7bf', opacity: 0.5}}
                                />
                                <div className="mt-2" style={{color: '#a6b7bf', fontSize: '14px'}}>
                                    {i18n.t('jt_no_results_found.text', 'No results found')}
                                </div>
                                <div style={{color: '#a6b7bf', fontSize: '12px', marginTop: '4px'}}>
                                    Try a different search term
                                </div>
                            </div>
                        ) : (
                            <div className="text-center" style={{padding: '32px 20px'}}>
                                <Icon
                                    name={config.searchIcon}
                                    size="lg"
                                    style={{color: '#009efb', opacity: 0.6}}
                                />
                                <div className="mt-2" style={{color: '#54667a', fontSize: '14px'}}>
                                    {i18n.t('jt_start_typing.text', 'Start typing to search...')}
                                </div>
                            </div>
                        )}
                    </div>
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
};

export default EntitySelector;
